// AttackSources.jsx
// ══════════════════════════════════════════════
// מקורות התקפה לפי מדינה - נתונים אמיתיים
// ══════════════════════════════════════════════

import { useEffect, useState } from "react";
import "../../../styles/navbar/CyberRoom/AttackSources.css";
import { useMamdani } from "../../../context/MamdaniContext";

const API_BASE = "https://api.anti-bot.ai";

// סוג התקפה לפי מדינה
const ATTACK_TYPE = {
  RU: "DDoS",
  CN: "APT",
  IR: "Wiper",
  KP: "Ransom",
  BY: "Proxy",
  US: "Scan",
  TR: "Phish",
  SA: "Spam",
  PK: "Botnet",
  IN: "Scan",
};

const COUNTRY_NAME = {
  RU: "רוסיה",
  CN: "סין",
  IR: "איראן",
  KP: "צפון קוריאה",
  BY: "בלארוס",
  US: "US",
  TR: "טורקיה",
  SA: "ערב הסעודית",
  PK: "פקיסטן",
  IN: "הודו",
  DE: "גרמניה",
  FR: "צרפת",
  GB: "בריטניה",
  UA: "אוקראינה",
  EG: "מצרים",
};

function threatColor(avgScore) {
  if (avgScore >= 75) return "#ff2244";
  if (avgScore >= 55) return "#ff8c00";
  return "#ffd700";
}

export default function AttackSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const { mamdaniMode: mm, data } = useMamdani();

  useEffect(() => {
    async function fetchSources() {
      try {
        const res  = await fetch(`${API_BASE}/api/cyber/threat-map`);
        const json = await res.json();
        if (json.success) {
          // מיון לפי כמות בוטים, מקסימום 6 מדינות
          const sorted = json.threats
            .filter(t => t.country_code)
            .sort((a, b) => b.bot_count - a.bot_count)
            .slice(0, 6);
          setSources(sorted);
        }
      } catch (err) {
        console.error("AttackSources fetch error:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSources();
    const iv = setInterval(fetchSources, 60000);
    return () => clearInterval(iv);
  }, []);

  // מצא את המקסימום לבר
  const maxCount = sources.length ? Math.max(...sources.map(s => s.bot_count)) : 1;

  return (
    <div className="as-wrapper">

      {/* כותרת */}
      <div className="as-header">
        <span className="as-dot" />
        <span className="as-title">{mm ? "Targeted Accounts" : "מקורות התקפה"}</span>
      </div>

      {/* רשימה — מוד ממדאני */}
      {mm && (
        <div className="as-list">
          {data.accounts.length === 0 && <div className="as-empty">No data</div>}
          {(() => {
            const maxBots = Math.max(...data.accounts.map(a => a.bots.length), 1);
            return data.accounts
              .slice()
              .sort((a, b) => b.bots.length - a.bots.length)
              .map(acc => {
                const color  = acc.bots.length >= 5 ? "#ff2244" : acc.bots.length >= 2 ? "#ff8c00" : "#ffd700";
                const barPct = Math.round((acc.bots.length / maxBots) * 100);
                return (
                  <div className="as-row" key={acc.username}>
                    <div className="as-left">
                      <span className="as-count" style={{ color }}>{acc.bots.length}</span>
                      <div className="as-info">
                        <span className="as-name">@{acc.username}</span>
                        <span className="as-type">bots detected</span>
                      </div>
                    </div>
                    <div className="as-right">
                      <span className="as-code" style={{ color, fontSize: 10 }}>
                        {acc.bots.length >= 5 ? "HIGH" : acc.bots.length >= 2 ? "MED" : "LOW"}
                      </span>
                    </div>
                    <div className="as-bar-wrap">
                      <div className="as-bar" style={{ width: `${barPct}%`, background: color }} />
                    </div>
                  </div>
                );
              });
          })()}
        </div>
      )}

      {/* רשימה — מוד רגיל */}
      {!mm && (
        <div className="as-list">
          {loading && <div className="as-loading">טוען...</div>}
          {!loading && sources.map((src) => {
            const code   = src.country_code;
            const color  = threatColor(src.avg_threat);
            const name   = COUNTRY_NAME[code] || code;
            const type   = ATTACK_TYPE[code]  || "Unknown";
            const barPct = Math.round((src.bot_count / maxCount) * 100);
            return (
              <div className="as-row" key={code}>
                <div className="as-left">
                  <span className="as-count" style={{ color }}>{src.bot_count.toLocaleString()}</span>
                  <div className="as-info">
                    <span className="as-name">{name}</span>
                    <span className="as-type">{type}</span>
                  </div>
                </div>
                <div className="as-right">
                  <img src={`https://flagcdn.com/20x15/${code.toLowerCase()}.png`} alt={code} className="as-flag" />
                  <span className="as-code">{code}</span>
                </div>
                <div className="as-bar-wrap">
                  <div className="as-bar" style={{ width: `${barPct}%`, background: color }} />
                </div>
              </div>
            );
          })}
          {!loading && sources.length === 0 && <div className="as-empty">אין נתונים</div>}
        </div>
      )}

    </div>
  );
}